import { useState, useEffect } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Card from '../../../components/ui/Card'
import Button from '../../../components/ui/Button'
import Input from '../../../components/ui/Input'
import SearchableSelect from '../../../components/ui/SearchableSelect'
import DoctorSearchSelect from '../../../components/ui/DoctorSearchSelect'
import api from '../../../lib/api'
import { formatCurrency, formatDate } from '../../../lib/formatters'
import toast from 'react-hot-toast'
import type { WorkOrder, PaginatedResponse } from '../../../types'

interface InvoiceItem {
  description: string
  hsnCode: string
  quantity: number
  unitPrice: number
  gstPercent: number
}

export default function NewInvoicePage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const workOrderId = searchParams.get('workOrderId')

  const [selectedDoctorId, setSelectedDoctorId] = useState('')
  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState(workOrderId || '')
  const [dueDate, setDueDate] = useState('')
  const [notes, setNotes] = useState('')
  const [items, setItems] = useState<InvoiceItem[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Set default due date to 30 days from now
  useEffect(() => {
    const date = new Date()
    date.setDate(date.getDate() + 30)
    setDueDate(date.toISOString().split('T')[0])
  }, [])

  // Fetch work orders for selected doctor
  const { data: workOrdersData } = useQuery({
    queryKey: ['work-orders', 'uninvoiced', selectedDoctorId],
    queryFn: async () => {
      if (!selectedDoctorId) return []
      const { data } = await api.get<PaginatedResponse<WorkOrder>>(
        `/work-orders?doctorId=${selectedDoctorId}&status=completed&limit=50`
      )
      return data.data || []
    },
    enabled: !!selectedDoctorId,
  })

  // Fetch selected work order details
  const { data: selectedWorkOrder } = useQuery({
    queryKey: ['work-orders', selectedWorkOrderId],
    queryFn: async () => {
      const { data } = await api.get(`/work-orders/${selectedWorkOrderId}`)
      return data.data as WorkOrder
    },
    enabled: !!selectedWorkOrderId,
  })

  // Auto-populate items from work order
  useEffect(() => {
    if (selectedWorkOrder?.items) {
      const invoiceItems: InvoiceItem[] = selectedWorkOrder.items.map((item) => ({
        description: item.catalogItem?.name || 'Work Item',
        hsnCode: item.catalogItem?.hsnCode || '999319',
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        gstPercent: 0,
      }))
      setItems(invoiceItems)
      if (selectedWorkOrder.doctorId && !selectedDoctorId) {
        setSelectedDoctorId(selectedWorkOrder.doctorId)
      }
    }
  }, [selectedWorkOrder])

  const workOrders = workOrdersData || []

  const addItem = () => {
    setItems([...items, {
      description: '',
      hsnCode: '999319',
      quantity: 1,
      unitPrice: 0,
      gstPercent: 0,
    }])
  }

  const updateItem = (index: number, updates: Partial<InvoiceItem>) => {
    const newItems = [...items]
    newItems[index] = { ...newItems[index], ...updates }
    setItems(newItems)
  }

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index))
  }

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0)
  }

  const calculateGST = () => {
    return items.reduce((sum, item) => {
      const itemTotal = item.quantity * item.unitPrice
      return sum + (itemTotal * item.gstPercent / 100)
    }, 0)
  }

  const calculateTotal = () => {
    return calculateSubtotal() + calculateGST()
  }

  const validate = () => {
    const newErrors: Record<string, string> = {}
    if (!selectedDoctorId) newErrors.doctor = 'Please select a doctor'
    if (!dueDate) newErrors.dueDate = 'Please select a due date'
    if (items.length === 0) newErrors.items = 'Please add at least one item'
    const hasEmptyItem = items.some(item => !item.description || item.unitPrice <= 0 || item.quantity <= 0)
    if (hasEmptyItem) newErrors.items = 'All items must have description, valid price and quantity'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        doctorId: selectedDoctorId,
        workOrderId: selectedWorkOrderId || undefined,
        dueDate: dueDate,
        notes: notes || undefined,
        items: items.map(item => ({
          description: item.description,
          hsnCode: item.hsnCode,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          gstPercent: item.gstPercent,
        })),
      }
      return api.post('/invoices', payload)
    },
    onSuccess: (response) => {
      const invoiceId = response.data?.data?.id
      toast.success('Invoice created successfully')
      if (invoiceId) {
        navigate(`/invoices/${invoiceId}`)
      } else {
        navigate('/invoices')
      }
    },
    onError: (error: any) => {
      const errorData = error.response?.data
      if (errorData?.code === 'INVOICE_ALREADY_EXISTS') {
        const invoiceNumber = errorData.existingInvoiceNumber || 'Unknown'
        toast.error(`An active invoice (${invoiceNumber}) already exists for this work order.`, { duration: 5000 })
      } else if (errorData?.code === 'DATABASE_MIGRATION_REQUIRED') {
        toast.error('Database migration required. Please contact admin.', { duration: 8000 })
      } else {
        toast.error(errorData?.detail || 'Failed to create invoice')
      }
    },
  })

  const handleSubmit = () => {
    if (validate()) {
      mutation.mutate()
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Create Invoice</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Generate a new GST-compliant invoice
          </p>
        </div>
        <Button variant="secondary" onClick={() => navigate('/invoices')}>
          <i className="fas fa-arrow-left mr-2"></i>
          Back
        </Button>
      </div>

      {/* Doctor & Work Order Selection */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Bill To
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <DoctorSearchSelect
            label="Doctor / Client"
            value={selectedDoctorId}
            onChange={(doctorId) => {
              setSelectedDoctorId(doctorId)
              setSelectedWorkOrderId('')
              setItems([])
              if (errors.doctor) setErrors({ ...errors, doctor: '' })
            }}
            error={errors.doctor}
            required
          />
          <SearchableSelect
            label="Work Order (Optional)"
            value={selectedWorkOrderId}
            onChange={setSelectedWorkOrderId}
            placeholder={selectedDoctorId ? 'Select work order to invoice...' : 'Select a doctor first'}
            icon="fa-clipboard-list"
            options={[
              { value: '', label: 'Create without work order' },
              ...workOrders.map((wo) => ({
                value: wo.id,
                label: `${wo.displayId} • ${wo.patientName || 'No patient'}`,
                sublabel: `${formatDate(wo.createdAt)} • ${wo.items?.length || 0} items • ${formatCurrency(wo.items?.reduce((s, i) => s + i.unitPrice * i.quantity, 0) || 0)} • ${wo.status}`,
              })),
            ]}
          />
        </div>
        {/* Work orders info when doctor is selected */}
        {selectedDoctorId && workOrders.length === 0 && (
          <p className="text-sm text-gray-500 mt-2">
            <i className="fas fa-info-circle mr-1"></i>
            No completed work orders found for this doctor. You can still create a manual invoice.
          </p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <Input
            label="Due Date *"
            type="date"
            value={dueDate}
            onChange={(e) => {
              setDueDate(e.target.value)
              if (errors.dueDate) setErrors({ ...errors, dueDate: '' })
            }}
            error={errors.dueDate}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Notes
            </label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes"
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      </Card>

      {/* Invoice Items */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Invoice Items
          </h2>
          <Button variant="secondary" onClick={addItem}>
            <i className="fas fa-plus mr-2"></i>
            Add Item
          </Button>
        </div>

        {errors.items && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
              <i className="fas fa-exclamation-circle"></i>
              {errors.items}
            </p>
          </div>
        )}

        {items.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <i className="fas fa-file-invoice text-4xl mb-3"></i>
            <p>No items added yet</p>
            <p className="text-sm">Click "Add Item" to start building your invoice</p>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((item, index) => (
              <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
                  <div className="sm:col-span-2">
                    <Input
                      label="Description"
                      value={item.description}
                      onChange={(e) => updateItem(index, { description: e.target.value })}
                      placeholder="Item description"
                    />
                  </div>
                  <Input
                    label="HSN Code"
                    value={item.hsnCode}
                    onChange={(e) => updateItem(index, { hsnCode: e.target.value })}
                    placeholder="999319"
                  />
                  <Input
                    label="Qty"
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) => updateItem(index, { quantity: parseInt(e.target.value) || 1 })}
                  />
                  <Input
                    label="Price (₹)"
                    type="number"
                    min="0"
                    value={item.unitPrice}
                    onChange={(e) => updateItem(index, { unitPrice: parseFloat(e.target.value) || 0 })}
                  />
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Input
                        label="GST %"
                        type="number"
                        min="0"
                        max="28"
                        value={item.gstPercent}
                        onChange={(e) => updateItem(index, { gstPercent: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
                <div className="mt-2 text-right text-sm text-gray-500">
                  Item Total: <span className="font-medium text-gray-900 dark:text-white">
                    {formatCurrency(item.quantity * item.unitPrice * (1 + item.gstPercent / 100))}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {items.length > 0 && (
          <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Subtotal:</span>
                <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(calculateSubtotal())}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">GST:</span>
                <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(calculateGST())}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-600">
                <span className="text-lg font-semibold text-gray-900 dark:text-white">Total:</span>
                <span className="text-xl font-bold text-primary-600">{formatCurrency(calculateTotal())}</span>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button variant="secondary" onClick={() => navigate('/invoices')}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} loading={mutation.isPending}>
          <i className="fas fa-check mr-2"></i>
          Create Invoice
        </Button>
      </div>
    </div>
  )
}
